import moment from "moment";
import clsx from "clsx";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faRotate, faXmark } from "@fortawesome/free-solid-svg-icons";

export type TScheduleItem = {
  installmentNumber: number;
  amount: number;
  paymentDate: string;
  isPaid: boolean;
  paidAt: string | null;
  retryCount: number;
  isFailed: boolean;
};

type Props = {
  schedule: TScheduleItem[];
};

type TStepStatus = "paid" | "next" | "retrying" | "failed" | "later";

const STATUS_STYLES: Record<
  TStepStatus,
  { dot: string; pill: string; icon?: IconDefinition }
> = {
  paid: {
    dot: "bg-emerald-500 text-white shadow-[0_0_0_4px_rgba(16,185,129,0.15)]",
    pill: "bg-emerald-500/10 text-emerald-600",
    icon: faCheck,
  },
  next: {
    dot: "bg-accent text-white shadow-[0_0_0_4px_rgba(99,102,241,0.2)]",
    pill: "bg-accent/10 text-accent",
  },
  retrying: {
    dot: "bg-amber-500 text-white shadow-[0_0_0_4px_rgba(245,158,11,0.18)]",
    pill: "bg-amber-500/10 text-amber-600",
    icon: faRotate,
  },
  failed: {
    dot: "bg-red-500 text-white shadow-[0_0_0_4px_rgba(239,68,68,0.15)]",
    pill: "bg-red-500/10 text-red-500",
    icon: faXmark,
  },
  later: {
    dot: "bg-surface text-light-gray border-2 border-edge-strong",
    pill: "bg-overlay text-light-gray",
  },
};

const FormatDate = (date: string | null) =>
  date ? moment(date).format("DD MMM YYYY") : "–";

/** Step timeline of a v2 plan: what's been taken and what's still due, with exact dates */
export default function V2PlanTimeline({ schedule }: Props) {
  if (!schedule.length) return null;

  const paid = schedule.filter((s) => s.isPaid);
  const amountTaken = paid.reduce((sum, s) => sum + s.amount, 0);
  const amountLeft = schedule
    .filter((s) => !s.isPaid)
    .reduce((sum, s) => sum + s.amount, 0);
  const nextNumber = schedule.find((s) => !s.isPaid && !s.isFailed)
    ?.installmentNumber;

  const GetStatus = (s: TScheduleItem): TStepStatus => {
    if (s.isPaid) return "paid";
    if (s.isFailed) return "failed";
    if (s.retryCount > 0) return "retrying";
    return s.installmentNumber === nextNumber ? "next" : "later";
  };

  const GetLabel = (s: TScheduleItem, status: TStepStatus) => {
    if (status === "paid") return "Paid";
    if (status === "failed") return "Failed";
    if (status === "retrying") return `Retrying (${s.retryCount}/7)`;
    return status === "next" ? "Next due" : "Pending";
  };

  return (
    <div
      className="mt-5 rounded-2xl bg-surface-elevated border border-edge px-5 py-5"
      data-testid="v2-plan-timeline"
    >
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-light-gray">
            Instalments
          </p>
          <p className="mt-1 text-[15px] font-bold text-text-primary">
            {paid.length} of {schedule.length} paid
          </p>
        </div>
        <p className="text-[12px] font-semibold text-text-secondary tabular-nums rounded-full bg-overlay px-3 py-1.5">
          £{amountTaken.toFixed(2)} taken · £{amountLeft.toFixed(2)} left
        </p>
      </div>

      {/* padding keeps the dot halos from being clipped by the scroll area */}
      <ol className="mt-4 -mx-2 flex overflow-x-auto px-3 pt-2 pb-3">
        {schedule.map((s, i) => {
          const status = GetStatus(s);
          const isLast = i === schedule.length - 1;
          const { dot, pill, icon } = STATUS_STYLES[status];

          return (
            <li
              key={s.installmentNumber}
              className="relative flex-1 min-w-[148px] pr-5"
              data-testid={`v2-step-${s.installmentNumber}`}
              data-status={status}
            >
              {/* line to the next step, green once this one is paid */}
              {!isLast ? (
                <span
                  className={clsx(
                    "absolute top-[15px] left-11 right-3 h-0.5 rounded-full",
                    status === "paid" ? "bg-emerald-500" : "bg-edge",
                  )}
                />
              ) : null}
              <span
                className={clsx(
                  "relative flex items-center justify-center w-8 h-8 rounded-full text-[12px] font-bold tabular-nums",
                  dot,
                )}
              >
                {icon ? (
                  <FontAwesomeIcon icon={icon} className="text-[12px]" />
                ) : (
                  s.installmentNumber
                )}
              </span>
              <p className="mt-4 text-[11px] font-semibold uppercase tracking-wider text-light-gray">
                Instalment {s.installmentNumber}
              </p>
              <p className="mt-1 text-[17px] font-bold text-heading-color tabular-nums">
                £{s.amount.toFixed(2)}
              </p>
              <span
                className={clsx(
                  "mt-2 inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold",
                  pill,
                )}
              >
                {GetLabel(s, status)}
              </span>
              <p className="mt-2 text-[12px] font-medium text-text-secondary">
                {status === "paid"
                  ? FormatDate(s.paidAt ?? s.paymentDate)
                  : `Due ${FormatDate(s.paymentDate)}`}
              </p>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
